// src/components/ProfileMedecinPage/DoctorPracticePanel/DoctorPracticePanel.jsx
"use client";

import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { message, Select } from "antd";
import useDoctorProfile from "@/ProfileDoctor/hooks/useDoctorProfile";
import { axiosGateway } from "@/api/axios";
import "./DoctorPracticePanel.css";
import Cookies from "js-cookie";
import doctorPracticeLocationIcon from "../../../../assets/icons/svg/doctor-practice-location.svg";
import doctorPracticeClockIcon from "../../../../assets/icons/svg/doctor-practice-clock.svg";

const IconModify = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path
      d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
    />
    <path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z" />
  </svg>
);

const pickFirst = (arr) => (Array.isArray(arr) && arr.length ? arr[0] : null);
const safe = (v) => (v == null ? "" : String(v).trim());
const SPOKEN_LANGUAGES = [
  "Arabe",
  "Français",
  "Anglais",
  "Espagnol",
  "Allemand",
  "Italien",
  "Portugais",
  "Turc",
  "Chinois",
  "Russe",
  "Japonais",
  "Coréen",
  "Hindi",
  "Ourdou",
  "Tamazight",
  "Néerlandais",
  "Grec",
  "Polonais",
  "Roumain",
  "Persan",
  "Bengali",
  "Swahili",
];

const extractInt = (v) => {
  if (v == null) return null;
  if (typeof v === "number") return Number.isFinite(v) ? Math.floor(v) : null;
  const s = String(v).trim();
  if (!s || s === "null" || s === "undefined") return null;
  const m = s.match(/-?\d+/);
  if (!m) return null;
  const n = parseInt(m[0], 10);
  return Number.isFinite(n) ? n : null;
};

const buildEmbedMapUrl = (query) =>
  `https://maps.google.com/maps?q=${encodeURIComponent(query)}&t=&z=15&ie=UTF8&iwloc=&output=embed`;

const parseAddressText = (addressText) => {
  const raw = safe(addressText);
  if (!raw) return { street: "", city: "", country: "" };
  const parts = raw.split(",").map((s) => s.trim()).filter(Boolean);
  return { street: parts[0] || "", city: parts[1] || "", country: parts[2] || "" };
};

const getRaw = (doctor) => doctor?.raw || {};

const uniqueStrings = (items) =>
  Array.from(new Set((items || []).map((item) => safe(item)).filter(Boolean)));

const normalizeTextList = (items) =>
  uniqueStrings(
    (Array.isArray(items) ? items : items ? [items] : []).map((item) =>
      typeof item === "string"
        ? item
        : item?.name || item?.label || item?.language || item?.title || item?.value
    )
  );

const normalizeCertificationsList = (items) =>
  (Array.isArray(items) ? items : items ? [items] : [])
    .map((item) => {
      if (typeof item === "string") return { title: item };
      return { title: safe(item?.title) || safe(item?.name) || safe(item?.label) || safe(item?.certificationName) };
    })
    .filter((item) => safe(item?.title));

const resolveName = (doctor, doctorInfo, clinic, clinics) => {
  const c = clinic || pickFirst(clinics);
  const raw = getRaw(doctor);
  const fromProfile = safe(c?.name) || safe(doctorInfo?.clinicName);
  if (fromProfile) return fromProfile;
  const fromRedux = safe(doctor?.clinicName) || safe(doctor?.clinic);
  if (fromRedux) return fromRedux;
  const fromRaw = safe(raw?.clinicName) || safe(raw?.clinic?.name) || safe(raw?.clinic);
  if (fromRaw) return fromRaw;
  const practiceType = safe(doctor?.practicePlace) || safe(raw?.practicePlace);
  if (practiceType) return practiceType;
  return "—";
};

const resolveAddress = (doctor, doctorInfo, clinic, clinics) => {
  const c = clinic || pickFirst(clinics);
  const raw = getRaw(doctor);
  const formatAddress = (value) => {
    if (!value || typeof value !== "object") return safe(value);
    return [value.street, value.city, value.country, value.zip]
      .map(safe)
      .filter(Boolean)
      .join(", ");
  };
  const fromProfile =
    formatAddress(c?.address) ||
    formatAddress(c?.clinicAddress) ||
    formatAddress(doctorInfo?.clinicAddress) ||
    formatAddress(doctorInfo?.address);
  if (fromProfile) return fromProfile;
  const fromRedux =
    formatAddress(doctor?.clinicAddress) ||
    formatAddress(doctor?.address) ||
    formatAddress(doctor?.practiceAddress);
  if (fromRedux) return fromRedux;
  const fromRaw =
    formatAddress(raw?.clinicAddress) ||
    formatAddress(raw?.practiceAddress) ||
    formatAddress(raw?.address) ||
    formatAddress(raw?.clinic?.clinicAddress) ||
    formatAddress(raw?.clinic?.address);
  if (fromRaw) return fromRaw;
  const street  = safe(c?.address?.street);
  const city    = safe(c?.address?.city)    || safe(doctor?.city)    || safe(raw?.city);
  const country = safe(c?.address?.country) || safe(doctor?.country) || safe(raw?.country);
  const parts   = [street, city, country].filter(Boolean).join(", ");
  if (parts) return parts;
  const cityOnly    = safe(doctor?.city)    || safe(raw?.city);
  const countryOnly = safe(doctor?.country) || safe(raw?.country);
  const cityCountry = [cityOnly, countryOnly].filter(Boolean).join(", ");
  if (cityCountry) return cityCountry;
  return "—";
};

const resolveYears = (doctor, clinic, doctorInfo) => {
  const raw = getRaw(doctor);
  const candidates = [
    doctorInfo?.yearsOfExperience, doctorInfo?.experienceYears, doctorInfo?.experience,
    clinic?.yearsOfExperience, clinic?.experienceYears, clinic?.experience,
    doctor?.yearsOfExperience, doctor?.experienceYears, doctor?.experience,
    raw?.yearsOfExperience, raw?.experienceYears, raw?.experience,
    raw?.years_of_experience, raw?.years,
  ];
  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined || candidate === "") continue;
    const n = extractInt(candidate);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return null;
};

const buildMapQuery = (doctor, doctorInfo, clinics) => {
  const c = pickFirst(clinics);
  const raw = getRaw(doctor);
  return (
    safe(c?.clinicAddress) || safe(doctorInfo?.clinicAddress) ||
    safe(doctor?.clinicAddress) || safe(doctor?.practiceAddress) ||
    safe(raw?.clinicAddress) || safe(raw?.practiceAddress) || safe(raw?.address) ||
    [safe(c?.address?.street), safe(c?.address?.city)].filter(Boolean).join(", ") ||
    [safe(doctor?.city) || safe(raw?.city), safe(doctor?.country) || safe(raw?.country)]
      .filter(Boolean).join(", ") ||
    "clinic"
  );
};

const getAuthDoctorFromStorage = () => {
  if (typeof window === "undefined") return null;
  const cookieKeys = [
    "auth", "session", "user", "currentUser", "currentDoctor", "doctor",
    "medilink_auth", "medilink_user", "profile", "doctorSession",
  ];
  for (const k of cookieKeys) {
    try {
      const raw = Cookies.get(k);
      if (!raw) continue;
      const obj = JSON.parse(raw);
      if (k === "doctorSession" && obj?.user) {
        const u = obj.user;
        if (u?.id || u?.doctorId || u?._id || u?.userId) return u;
      }
      const cand =
        obj?.doctor || obj?.currentDoctor || obj?.actor?.doctor || obj?.profile?.doctor ||
        (obj?.role === "doctor" ? obj : null) || (obj?.type === "doctor" ? obj : null) || null;
      if (cand && (cand.doctorId || cand.id || cand._id || cand.userId)) return cand;
    } catch (_) {}
  }
  const lsKeys = [
    "auth", "session", "user", "currentUser", "currentDoctor", "doctor",
    "medilink_auth", "medilink_user", "profile", "doctorSession",
    "persist:auth", "persist:doctor", "persist:user",
  ];
  for (const k of lsKeys) {
    try {
      const raw = window.localStorage.getItem(k);
      if (!raw) continue;
      const obj = JSON.parse(raw);
      const inner =
        typeof obj === "object" && obj !== null
          ? obj
          : (() => { try { return JSON.parse(obj); } catch { return null; } })();
      if (!inner) continue;
      if (k === "doctorSession" && inner?.user) {
        const u = inner.user;
        if (u?.id || u?.doctorId || u?._id || u?.userId) return u;
      }
      if (inner?.doctor) {
        try {
          const d = typeof inner.doctor === "string" ? JSON.parse(inner.doctor) : inner.doctor;
          if (d && (d.doctorId || d.id || d._id || d.userId)) return d;
        } catch (_) {}
      }
      const cand =
        inner?.currentDoctor || inner?.actor?.doctor || inner?.profile?.doctor ||
        (inner?.role === "doctor" ? inner : null) || (inner?.type === "doctor" ? inner : null) || null;
      if (cand && (cand.doctorId || cand.id || cand._id || cand.userId)) return cand;
      if (inner?.doctorId || inner?.id || inner?._id) return inner;
    } catch (_) {}
  }
  return null;
};

// ─────────────────────────────────────────────────────────────────────────────
export default function DoctorPracticePanel({
  doctor = {},
  onReloadDoctor,
  onSaveYearsOfExperience,
}) {
  const { t, i18n } = useTranslation();
  const isRTL = (i18n.language || "").startsWith("ar");
  const [isEdit, setIsEdit] = useState(false);
  const [localErr, setLocalErr] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const authDoctor = useMemo(() => getAuthDoctorFromStorage(), []);

  const mergedDoctor = useMemo(
    () => ({
      ...(authDoctor || {}),
      ...(doctor || {}),
      raw: {
        ...(authDoctor?.raw || authDoctor || {}),
        ...(doctor?.raw || {}),
      },
    }),
    [authDoctor, doctor]
  );

  const doctorId = useMemo(() => {
    const id =
      mergedDoctor?.doctorId ||
      mergedDoctor?.doctor_id ||
      mergedDoctor?.id ||
      mergedDoctor?._id ||
      mergedDoctor?.userId ||
      mergedDoctor?.raw?.id ||
      mergedDoctor?.raw?.doctorId ||
      mergedDoctor?.raw?._id ||
      null;
    return id ? String(id) : null;
  }, [mergedDoctor]);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // 🔍 DEBUG — colle ce que tu vois dans la console ici
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  useEffect(() => {
    console.group("╔══ [DoctorPracticePanel] DEBUG ══╗");

    console.group("🔑 Candidats doctorId:");
    console.groupEnd();

    if (doctorId) {
    } else {
      console.error("❌ doctorId EST NULL — fetch/upsert vont échouer !");
      console.warn("👉 Le prop 'doctor' doit contenir 'id', 'doctorId', '_id' ou 'userId'.");
    }

    console.group("🍪 Cookies auth:");
    console.groupEnd();

    console.groupEnd();
  }, [doctor, authDoctor, mergedDoctor, doctorId]);
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  const {
    doctorInfo, clinic, certifications, clinics,
    languages, loading, error,
    saving, saveError, reload, saveDoctorInfo, forceSaveDoctorInfo, patchDoctorInfo,
  } = useDoctorProfile(doctorId);

  const clinicNameDisplay = useMemo(
    () => resolveName(mergedDoctor, doctorInfo, clinic, clinics),
    [mergedDoctor, doctorInfo, clinic, clinics]
  );

  const addressDisplay = useMemo(
    () => resolveAddress(mergedDoctor, doctorInfo, clinic, clinics),
    [mergedDoctor, doctorInfo, clinic, clinics]
  );

  const officeHoursTextFallback = useMemo(() => {
    const raw = getRaw(mergedDoctor);
    return (
      safe(clinic?.officeHours) || safe(doctorInfo?.officeHours) ||
      safe(mergedDoctor?.officeHours) || safe(mergedDoctor?.hours) ||
      safe(raw?.officeHours) || safe(raw?.hours) || ""
    );
  }, [clinic, doctorInfo, mergedDoctor]);

  const years = useMemo(
    () => resolveYears(mergedDoctor, clinic, doctorInfo),
    [mergedDoctor, clinic, doctorInfo]
  );

  const mapUrl = useMemo(
    () => buildEmbedMapUrl(buildMapQuery(mergedDoctor, doctorInfo, clinics)),
    [mergedDoctor, doctorInfo, clinics]
  );

  const navigateUrl = useMemo(
    () => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(buildMapQuery(mergedDoctor, doctorInfo, clinics))}`,
    [mergedDoctor, doctorInfo, clinics]
  );

  const certList = useMemo(
    () => {
      const raw = getRaw(mergedDoctor);
      const fromHook = normalizeCertificationsList(certifications);
      if (fromHook.length) return fromHook;
      return normalizeCertificationsList(mergedDoctor?.certifications || raw?.certifications || raw?.diplomas);
    },
    [certifications, mergedDoctor]
  );
  const langList = useMemo(
    () => {
      const raw = getRaw(mergedDoctor);
      const fromHook = normalizeTextList(languages);
      if (fromHook.length) return fromHook;
      return normalizeTextList(mergedDoctor?.languages || mergedDoctor?.spokenLanguages || raw?.languages || raw?.spokenLanguages);
    },
    [languages, mergedDoctor]
  );

  const insuranceList = useMemo(() => {
    const raw = getRaw(mergedDoctor);
    return normalizeTextList(
      doctorInfo?.insurances ||
      mergedDoctor?.acceptedInsurance ||
      mergedDoctor?.insuranceCoverage ||
      mergedDoctor?.insurances ||
      raw?.acceptedInsurance ||
      raw?.insuranceCoverage ||
      raw?.insurances
    );
  }, [doctorInfo?.insurances, mergedDoctor]);

  const hasFallbackProfileData = useMemo(() => {
    return Boolean(
      safe(clinicNameDisplay) !== "—" ||
      safe(addressDisplay) !== "—" ||
      safe(officeHoursTextFallback) ||
      langList.length ||
      certList.length ||
      years != null
    );
  }, [clinicNameDisplay, addressDisplay, officeHoursTextFallback, langList, certList, years]);

  const [form, setForm] = useState({
    clinicName: "", addressText: "", officeHoursText: "",
    yearsOfExperience: "", newLang: "", newCertTitle: "",
  });
  const [draftLangs, setDraftLangs] = useState([]);
  const [draftCerts, setDraftCerts] = useState([]);
  const [insuranceProviders, setInsuranceProviders] = useState([]);
  const [draftInsurances, setDraftInsurances] = useState([]);

  useEffect(() => {
    let active = true;
    axiosGateway.get("/insurance-providers")
      .then(({ data }) => active && setInsuranceProviders(Array.isArray(data) ? data : []))
      .catch(() => active && setInsuranceProviders([]));
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!isEdit && years != null)
      setForm((prev) => ({ ...prev, yearsOfExperience: String(years) }));
  }, [years, isEdit]);

  useEffect(() => {
    if (!isEdit && addressDisplay && addressDisplay !== "—")
      setForm((prev) => ({ ...prev, addressText: addressDisplay }));
  }, [addressDisplay, isEdit]);

  const startEdit = () => {
    setLocalErr("");
    setIsEdit(true);
    setForm({
      clinicName: clinicNameDisplay !== "—" ? clinicNameDisplay : "",
      addressText: addressDisplay !== "—" ? addressDisplay : "",
      officeHoursText: officeHoursTextFallback,
      yearsOfExperience: years != null ? String(years) : "",
      newLang: "", newCertTitle: "",
    });
    setDraftLangs(langList);
    setDraftCerts(certList);
    setDraftInsurances(normalizeTextList(doctorInfo?.insurances || mergedDoctor?.acceptedInsurance || mergedDoctor?.insuranceCoverage || mergedDoctor?.insurances || getRaw(mergedDoctor)?.acceptedInsurance || getRaw(mergedDoctor)?.insuranceCoverage || getRaw(mergedDoctor)?.insurances));
  };

  const cancelEdit = async () => {
    setLocalErr("");
    setIsEdit(false);
    await Promise.allSettled([reload?.()]);
  };

  const addLang = () => {
    const v = safe(form.newLang);
    if (!v) return;
    const norm = (s) => safe(s).toLowerCase();
    if ((draftLangs || []).some((x) => norm(x) === norm(v))) return;
    setDraftLangs((p) => [...p, v]);
    setForm((s) => ({ ...s, newLang: "" }));
  };

  const removeLangAt = (idx) => setDraftLangs((p) => p.filter((_, i) => i !== idx));

  const addCertInline = () => {
    const title = safe(form.newCertTitle);
    if (!title) {
      setLocalErr(t("doctorPractice.errors.certTitleRequired"));
      return;
    }
    setLocalErr("");
    setDraftCerts((p) => [...p, { title }]);
    setForm((s) => ({ ...s, newCertTitle: "" }));
  };

  const removeCertAt = (idx) => setDraftCerts((p) => p.filter((_, i) => i !== idx));

  const saveAll = async () => {
    setLocalErr("");
    setIsSaving(true);
    try {
      if (!doctorId) throw new Error(t("doctorPractice.errors.missingDoctorId"));

      const cleanedLangs = (draftLangs || []).map((l) => safe(l)).filter(Boolean);
      const cleanedCerts = (draftCerts || [])
        .map((c) => ({
          title: safe(c?.title),
          issuingBody: c?.issuingBody ? safe(c.issuingBody) : undefined,
        }))
        .filter((c) => c.title);

      let clinicNameVal = safe(form.clinicName);
      const clinicAddressRaw = safe(form.addressText);
      const officeHoursTextVal = safe(form.officeHoursText);
      const parsedAddr = parseAddressText(clinicAddressRaw);
      const currentClinic = clinic || pickFirst(clinics);
      const currentAddress = currentClinic?.address || {};
      const rawDoctor = getRaw(mergedDoctor);
      const addr = {
        street: parsedAddr.street || safe(currentAddress.street),
        city:
          parsedAddr.city ||
          safe(currentAddress.city) ||
          safe(mergedDoctor.city) ||
          safe(rawDoctor.city),
        country:
          parsedAddr.country ||
          safe(currentAddress.country) ||
          safe(mergedDoctor.country) ||
          safe(rawDoctor.country),
      };

      if (!clinicNameVal) clinicNameVal = addr.street || addr.city || "Clinic";

      const hasClinic =
        clinicNameVal || clinicAddressRaw || addr.street ||
        addr.city || addr.country || officeHoursTextVal;

      const nextClinics = hasClinic
        ? [{
            name: clinicNameVal,
            address: { street: addr.street, city: addr.city, country: addr.country, zip: "" },
            officeHours: officeHoursTextVal,
          }]
        : [];

      const yearsValue =
        form.yearsOfExperience !== "" && form.yearsOfExperience != null
          ? parseInt(String(form.yearsOfExperience), 10)
          : null;

      const upsertPayload = {
        certifications: cleanedCerts,
        clinics: nextClinics,
        languages: cleanedLangs,
        insurances: uniqueStrings(draftInsurances),
        officeHours: Array.isArray(doctorInfo?.officeHours) ? doctorInfo.officeHours : [],
      };

      // 🔍 DEBUG saveAll
      console.group("╔══ [DPP] saveAll ══╗");
      console.groupEnd();

      // ✅ Utiliser forceSaveDoctorInfo pour une sauvegarde forcée avec vérification
      await forceSaveDoctorInfo(upsertPayload);
      if (Number.isFinite(yearsValue)) {
        try {
          await onSaveYearsOfExperience?.(yearsValue);
        } catch (yearsError) {
          console.warn("Doctor info saved, but experience update failed:", yearsError);
        }
      }

      // ✅ NOUVEAU: Afficher les valeurs sauvegardées dans une notification
      const savedValuesMsg = [];
      if (cleanedCerts.length > 0) {
        savedValuesMsg.push(`📋 ${cleanedCerts.length} cert(s): ${cleanedCerts.map(c => c.title).join(", ")}`);
      }
      if (cleanedLangs.length > 0) {
        savedValuesMsg.push(`🗣️ ${cleanedLangs.length} lang(s): ${cleanedLangs.join(", ")}`);
      }
      if (clinicNameVal && clinicNameVal !== "Clinic") {
        savedValuesMsg.push(`🏥 Clinic: ${clinicNameVal}`);
      }
      if (clinicAddressRaw) {
        savedValuesMsg.push(`📍 Address: ${clinicAddressRaw}`);
      }
      if (officeHoursTextVal) {
        savedValuesMsg.push(`⏰ Hours: ${officeHoursTextVal}`);
      }
      if (Number.isFinite(yearsValue)) {
        savedValuesMsg.push(`📅 Experience: ${yearsValue} years`);
      }

      const successMsg = savedValuesMsg.length > 0 
        ? savedValuesMsg.join("\n")
        : "✅ Data saved successfully";

      message.success({
        content: (
          <div>
            <strong>✅ Saved Successfully!</strong>
            <div style={{ marginTop: "8px", fontSize: "12px", whiteSpace: "pre-line" }}>
              {successMsg}
            </div>
          </div>
        ),
        duration: 5,
        style: { marginTop: "20px" }
      });

      await Promise.allSettled([reload?.(), onReloadDoctor?.()]);
      window.dispatchEvent(new Event("doctorProfileUpdated"));
      setIsEdit(false);
    } catch (e) {
      const errMsg =
        e?.response?.data?.message || e?.message || t("doctorPractice.errors.saveFailed");

      // 🔍 DEBUG erreur
      console.group("╔══ [DPP] saveAll ERROR ══╗");
      console.error("message   :", errMsg);
      console.error("status    :", e?.response?.status);
      console.error("resp data :", e?.response?.data);
      console.error("full error:", e);
      console.groupEnd();

      // ✅ NOUVEAU: Notification d'erreur
      message.error({
        content: (
          <div>
            <strong>❌ Save Failed</strong>
            <div style={{ marginTop: "8px", fontSize: "12px" }}>
              {errMsg}
            </div>
          </div>
        ),
        duration: 5,
        style: { marginTop: "20px" }
      });

      setLocalErr(errMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const showSaving = saving || isSaving;

  if (!isEdit) {
    return (
      <div className="dpp2-wrap" dir={isRTL ? "rtl" : "ltr"}>
        <div className="dpp2-card dpp2-cardView">
          <div className="dpp2-viewHead">
            <div className="dpp2-viewPanelTitle">{t("doctorClinicInfo.panelTitle")}</div>
            <button
              className="dpp2-viewEditBtn"
              type="button"
              onClick={startEdit}
              title={t("doctorClinicInfo.editProfile")}
              disabled={!doctorId}
            >
              <span className="dpp2-viewEditText">{t("doctorClinicInfo.editProfile")}</span>
              <IconModify />
            </button>
          </div>

          <div className="dpp2-viewSection dpp2-viewSectionClinic">
            <div className="dpp2-viewClinicText">
              <div className="dpp2-viewSectionTitle">{t("doctorPractice.title")}</div>

              <div className="dpp2-row dpp2-rowCompact">
                <span className="dpp2-viewIcon" aria-hidden>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M12 21C12 21 19 15.4 19 10A7 7 0 1 0 5 10C5 15.4 12 21 12 21Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.7" />
                  </svg>
                </span>
                <div className="dpp2-col">
                  <div className="dpp2-viewPrimary">{clinicNameDisplay}</div>
                  <div className="dpp2-muted">{addressDisplay}</div>
                </div>
              </div>

              <div className="dpp2-row dpp2-rowCompact dpp2-viewInsuranceRow">
                <span className="dpp2-viewIcon" aria-hidden>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M12 3L18 5.75V11C18 15.25 15.3 18.88 12 20C8.7 18.88 6 15.25 6 11V5.75L12 3Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M9.5 11.75L11.25 13.5L14.75 10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <div className="dpp2-col">
                  <div className="dpp2-viewInsuranceTitle">{t("doctorClinicInfo.insuranceCoverage")}</div>
                  {insuranceList.length ? (
                    insuranceList.map((insurance) => (
                      <div className="dpp2-viewInsuranceBadge" key={insurance}>
                        <span className="dpp2-viewInsuranceChip" aria-hidden>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M12 3L18 5.75V11C18 15.25 15.3 18.88 12 20C8.7 18.88 6 15.25 6 11V5.75L12 3Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                        <span className="dpp2-viewInsuranceName">{insurance}</span>
                        <span className="dpp2-viewInsuranceStatus">{t("doctorClinicInfo.acceptedInsurance")}</span>
                      </div>
                    ))
                  ) : (
                    <div className="dpp2-viewInsuranceEmpty">
                      Aucune assurance conventionnée.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="dpp2-viewMapCard">
              <div className="dpp2-map">
                <iframe src={mapUrl} loading="lazy" title="clinic-map" allowFullScreen />
              </div>
              <a className="dpp2-viewNavigate" href={navigateUrl} target="_blank" rel="noreferrer">
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none">
                  <path d="M3 11.5L20.5 3L12 20.5L10 13.5L3 11.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {t("doctorClinicInfo.navigate")}
              </a>
            </div>
          </div>

          <div className="dpp2-viewSection dpp2-viewInlineSection">
            <div className="dpp2-viewInlineTitleWrap">
              <span className="dpp2-viewInlineIcon" aria-hidden>
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path d="M3 8L12 4L21 8L12 12L3 8Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M7 10.5V14.5C7 15.1 8.8 17 12 17C15.2 17 17 15.1 17 14.5V10.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <div className="dpp2-viewInlineLabel">{t("doctorPractice.certs.title")}</div>
            </div>
            <div className="dpp2-chips">
              {certList.length ? (
                certList.map((c, idx) => (
                  <span key={`${c?.title || "cert"}-${idx}`} className="dpp2-viewChip">
                    <span>{c?.title || "—"}</span>
                    <span className="dpp2-viewChipX" aria-hidden>×</span>
                  </span>
                ))
              ) : (
                <div className="dpp2-muted">—</div>
              )}
            </div>
          </div>

          <div className="dpp2-viewSection dpp2-viewInlineSection">
            <div className="dpp2-viewInlineTitleWrap">
              <span className="dpp2-viewInlineIcon" aria-hidden>
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m5 8 6 6" />
                    <path d="m4 14 6-6 2-3" />
                    <path d="M2 5h12" />
                    <path d="M7 2h1" />
                    <path d="m22 22-5-10-5 10" />
                    <path d="M14 18h6" />
                </svg>
              </span>
              <div className="dpp2-viewInlineLabel">{t("doctorPractice.languages.title")}</div>
            </div>
            <div className="dpp2-chips">
              {langList.length ? (
                langList.map((l, idx) => (
                  <span key={`${l}-${idx}`} className="dpp2-viewChip">
                    <span>{l}</span>
                    <span className="dpp2-viewChipX" aria-hidden>×</span>
                  </span>
                ))
              ) : (
                <div className="dpp2-muted">—</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dpp2-wrap" dir={isRTL ? "rtl" : "ltr"}>
      <div className="dpp2-card dpp2-cardView dpp2-cardEdit">
        <div className="dpp2-viewHead">
          <div className="dpp2-viewPanelTitle">{t("doctorClinicInfo.panelTitle")}</div>
          <div className="dpp2-headActions">
            <button className="dpp2-btnGhost dpp2-btnGhostView" type="button" onClick={cancelEdit} disabled={showSaving}>
              {t("doctorPractice.actions.cancel")}
            </button>
            <button className="dpp2-btnPrimary dpp2-btnPrimaryView" type="button" onClick={saveAll} disabled={showSaving}>
              {showSaving ? t("doctorPractice.actions.saving") : t("doctorPractice.actions.save")}
            </button>
          </div>
        </div>

        <div className="dpp2-viewSection dpp2-viewSectionClinic">
          <div className="dpp2-viewClinicText">
            <div className="dpp2-viewSectionTitle">{t("doctorPractice.title")}</div>
            <div className="dpp2-row dpp2-rowCompact dpp2-editClinicRow">
              <span className="dpp2-viewIcon" aria-hidden>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M12 21C12 21 19 15.4 19 10A7 7 0 1 0 5 10C5 15.4 12 21 12 21Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.7" />
                </svg>
              </span>
              <div className="dpp2-col dpp2-editCol">
                <div className="dpp2-editFieldLabel">{t("doctorPractice.placeholders.clinicName")}</div>
                <input
                  className="dpp2-inputInline dpp2-inputInline--title dpp2-inputInline--view"
                  value={form.clinicName}
                  onChange={(e) => setForm((s) => ({ ...s, clinicName: e.target.value }))}
                  placeholder={t("doctorPractice.placeholders.clinicName")}
                />
                <input
                  className="dpp2-inputInline dpp2-inputInline--view"
                  value={form.addressText}
                  onChange={(e) => setForm((s) => ({ ...s, addressText: e.target.value }))}
                  placeholder={t("doctorPractice.placeholders.address")}
                />
              </div>
            </div>


          </div>

          <div className="dpp2-viewMapCard dpp2-viewMapCardEdit">
            <div className="dpp2-map">
              <iframe src={mapUrl} loading="lazy" title="clinic-map" allowFullScreen />
            </div>
            <a className="dpp2-viewNavigate" href={navigateUrl} target="_blank" rel="noreferrer">
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none">
                <path d="M3 11.5L20.5 3L12 20.5L10 13.5L3 11.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {t("doctorClinicInfo.navigate")}
            </a>
          </div>
        </div>

        <div className="dpp2-viewSection dpp2-viewInlineSection dpp2-viewInlineSectionEdit">
          <div className="dpp2-viewInlineTitleWrap dpp2-viewInlineTitleWrapEdit">
            <div className="dpp2-viewInlineLabel">Insurance</div>
          </div>
          <div className="dpp2-editInlineBody">
            <Select
              mode="multiple"
              allowClear
              showSearch
              placeholder="Select accepted insurance providers"
              value={draftInsurances}
              onChange={setDraftInsurances}
              options={insuranceProviders.map((provider) => ({ label: provider, value: provider }))}
              style={{ width: "100%" }}
            />
          </div>
        </div>

        <div className="dpp2-viewSection dpp2-viewInlineSection dpp2-viewInlineSectionEdit">
          <div className="dpp2-viewInlineTitleWrap dpp2-viewInlineTitleWrapEdit">
            <span className="dpp2-viewInlineIcon" aria-hidden>
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="m5 8 6 6" />
                <path d="m4 14 6-6 2-3" />
                <path d="M2 5h12" />
                <path d="M7 2h1" />
                <path d="m22 22-5-10-5 10" />
                <path d="M14 18h6" />
              </svg>
            </span>
            <div className="dpp2-viewInlineLabel">{t("doctorPractice.languages.title")}</div>
          </div>
          <div className="dpp2-editInlineBody">
            <div className="dpp2-chips dpp2-editChips">
              {draftLangs.map((l, idx) => (
                <span className="dpp2-viewChip dpp2-viewChipEditable" key={`${l}-${idx}`}>
                  <span>{l}</span>
                  <button type="button" className="dpp2-chipX dpp2-chipXView" onClick={() => removeLangAt(idx)}>×</button>
                </span>
              ))}
            </div>
            <div className="dpp2-editAddRow">
              <Select
                className="dpp2-language-select"
                showSearch
                allowClear
                optionFilterProp="label"
                value={form.newLang || undefined}
                onChange={(value) => setForm((s) => ({ ...s, newLang: value || "" }))}
                placeholder={t("doctorPractice.placeholders.addLanguage")}
                options={SPOKEN_LANGUAGES.map((language) => ({ label: language, value: language }))}
              />
              <button className="dpp2-addBtn dpp2-addBtnView" type="button" onClick={addLang}>+</button>
            </div>
          </div>
        </div>

        <div className="dpp2-viewSection dpp2-viewInlineSection dpp2-viewInlineSectionEdit">
          <div className="dpp2-viewInlineTitleWrap dpp2-viewInlineTitleWrapEdit">
            <span className="dpp2-viewInlineIcon" aria-hidden>
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M3 8L12 4L21 8L12 12L3 8Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M7 10.5V14.5C7 15.1 8.8 17 12 17C15.2 17 17 15.1 17 14.5V10.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <div className="dpp2-viewInlineLabel">{t("doctorPractice.certs.title")}</div>
          </div>
          <div className="dpp2-editInlineBody">
            <div className="dpp2-chips dpp2-editChips">
              {draftCerts.map((c, idx) => (
                <span className="dpp2-viewChip dpp2-viewChipEditable" key={`${c?.title || "cert"}-${idx}`}>
                  <span>{c?.title || "—"}</span>
                  <button type="button" className="dpp2-chipX dpp2-chipXView" onClick={() => removeCertAt(idx)}>×</button>
                </span>
              ))}
            </div>
            <div className="dpp2-editAddRow">
              <input
                className="dpp2-inputInline dpp2-inputInline--view"
                value={form.newCertTitle}
                onChange={(e) => setForm((s) => ({ ...s, newCertTitle: e.target.value }))}
                placeholder={t("doctorPractice.placeholders.certTitle")}
                onKeyDown={(e) => e.key === "Enter" && addCertInline()}
              />
              <button className="dpp2-addBtn dpp2-addBtnView" type="button" onClick={addCertInline}>+</button>
            </div>
          </div>
        </div>

        {(saveError || localErr || (error && !loading && !hasFallbackProfileData)) ? (
          <div className="dpp2-err dpp2-errView">
            {String(localErr || saveError?.message || saveError || error?.message || error)}
          </div>
        ) : null}
      </div>
    </div>
  );
}